import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { get, includes } from 'lodash';
import { acquireAccessToken, getCurrentAccount } from 'app/msal-utils';
import { getGcRole, resolveActiveRole } from 'app/active-role-utils';
import { GC_ROLES, HEADER_NAMES, SESSION_STORAGE_KEYS } from 'app/constants';
import config from 'app/config';

const baseQuery = fetchBaseQuery({
  baseUrl: config.API_BASE_URL,
  prepareHeaders: (headers, { endpoint }) => {
    return acquireAccessToken({ forceLoginRedirect: false }).then((token) => {
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set(HEADER_NAMES.CSRF, '1');

      // A guest-checkpoint (cookie) user has no MSAL account, so getCurrentAccount() is null and
      // this stays a no-op for them, same as before. For an MSAL user, honor their own switchable
      // active role when it is itself a GC role — an Admins+GuestPermit user who picked GuestPermit
      // in the header must act as GuestPermit here too, not silently escalate to Admins — and only
      // fall back to the best held GC role when the active role isn't a GC role at all (e.g. they
      // never switched, or picked something like Managers that has no bearing on /gc).
      const heldRoles = get(getCurrentAccount(), 'idTokenClaims.roles', []);
      const storedRole = sessionStorage.getItem(SESSION_STORAGE_KEYS.ACTIVE_ROLE);
      const activeRole = resolveActiveRole(storedRole, heldRoles);
      const gcRole = includes(GC_ROLES, activeRole) ? activeRole : getGcRole(heldRoles);
      if (gcRole) {
        headers.set(HEADER_NAMES.ACTIVE_ROLE, gcRole);
      }

      return headers;
    });
  },
  credentials: 'include'
});

const customBaseQuery = async (args, api, extraOptions) => {
  let response = await baseQuery(args, api, extraOptions);

  let respStatus = response?.error?.status;

  // A signed-in Entra user's cached ID token can look valid to MSAL yet already be rejected by the
  // backend as expired (see app/msal-token.js's near-expiry refresh, which still leaves a gap up to
  // an hour into a session) - force a refresh and retry once before treating this as a real session
  // expiry. A guest-checkpoint (cookie) user has no MSAL account, so getCurrentAccount() is null and
  // this stays a no-op for them, same as before.
  if (respStatus === 401 && getCurrentAccount()) {
    try {
      // Captured *before* forcing the refresh so it reflects the token prepareHeaders actually
      // attached to the just-failed request. MSAL's own acquireTokenSilent request-deduplication
      // does not key on forceRefresh (see app/msal-token.js's own comment on the same MSAL quirk
      // in reverse), so a forced refresh that lands while an ordinary silent call for the same
      // account/scopes is already in flight can resolve to that same, already-rejected token -
      // retrying with it would just 401 again, so skip straight back to the original failure
      // instead.
      const rejectedToken = await acquireAccessToken({ forceLoginRedirect: false });
      const refreshedToken = await acquireAccessToken({ forceRefresh: true, forceLoginRedirect: false });
      if (refreshedToken && refreshedToken !== rejectedToken) {
        response = await baseQuery(args, api, extraOptions);
        respStatus = response?.error?.status;
      }
    } catch (refreshError) {
      // Keep the original 401 - falls through to the /gc/login redirect below, same as if the
      // refresh had simply not helped.
    }
  }

  if (respStatus && [401,403].includes(respStatus)) {
    window.location.href = window.location.origin + "/gc/login";
  }

  return response;
};

export const mixedAuthLubaApi = createApi({
  reducerPath: 'mixedAuthLubaApi',
  baseQuery: customBaseQuery,
  tagTypes: ['GuestCheckpoints', 'GuestPermits'],
  endpoints: (builder) => ({
    login: builder.mutation({
      query({ body }) {
        return {
          url: `/GuestCheckpoint/login`,
          method: 'POST',
          body,
        };
      },
    }),
    logout: builder.mutation({
      query() {
        return {
          url: `/GuestCheckpoint/logout`,
          method: 'POST',
        };
      },
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
} = mixedAuthLubaApi;
